import type { WelcomeSupportItem, WelcomeCategoryCard } from '@/types/domains/WelcomeType';

/**
 * 初めての方へ。支給デザイン `welcome.html` の `sec_welcome`。
 *
 * **旧 `WELCOME_SECTION_LIST`（4ブロックの読み物）は支給に対応が無いので置き換えた。**
 * とくに「オリエンタルバイオ・オンラインショップとは」は支給に相当セクションが無く
 * （全便の支給HTMLを grep して0件）、リンク2本（商品一覧・お客様の声）が
 * `product-search` と `voice-search` の2セクションに分解されている。
 * 支給を正とし、本文はこの回で落とす（PR に一覧を明記。2026-08-13 事業側判断）。
 *
 * **見出しと本文は支給HTMLをそのまま持つ。** `<br class="pc-only">` / `sp-only` / `ipad-only` で
 * 改行位置を出し分けており、プレーンテキストにすると折り返しが崩れる。表示側は `v-html` で出す
 * （出どころは支給デザインだけで、ユーザー入力は混ざらない）。
 */

/** 導入。支給の `welcome-head` */
export const WELCOME_HEAD = {
  title: `ようこそ、<br class="ipad-only" />オリエンタルバイオ<span>の</span><br />
オンラインショップへ`,
  text: `オリエンタルバイオのオンラインショップをご訪問いただき、<br class="ipad-only" />誠にありがとうございます。<br /><br />
初めての方にも安心してお買い物をお楽しみいただけるよう、<br class="pc-only" />
当ショップのサービスやご便利なガイドページをご案内いたします。`,
  image: '/image/welcome/img_page-welcome001.png',
  spImage: '/image/welcome/img_page-welcome001_sp.png',
} as const;

/**
 * 3つのポイント。支給の `welcome-support`。
 *
 * **見出しは「3つのポイント」だが item は4つある。** 4件目の番号は支給が `sp-only` に
 * しているので PC では出ず、数が合わなくなるのは SP だけ。支給どおりに置く。
 */
export const WELCOME_SUPPORT_TITLE = `オリエンタルバイオ
<br class="sp-only" />オンラインショップの` as const;

export const WELCOME_SUPPORT_ITEMS: ReadonlyArray<WelcomeSupportItem> = [
  {
    point: 1,
    icon: '/image/welcome-icon_member.png',
    title: '無料の会員登録が便利！',
    text: `お届けサイクル変更など便利な<br class="pc-only" />
マイページ機能が利用できます。<br class="pc-only" />
キャンペーン情報もいち早く<br class="sp-only" />お届け!`,
    button: {
      label: '新規会員登録',
      to: '/register',
      variant: 'green',
      icon: '/image/common/header/icon_member-regist_white.png',
    },
  },
  {
    point: 2,
    icon: '/image/welcome-icon_regular.png',
    title: '定期コースがお得！',
    text: `特別価格での購入や<br />
送料無料など5大特典付き!<br />
便利にお買い物いただけます。`,
    button: { label: '定期コースについて', to: '/regular', variant: 'outline' },
  },
  {
    point: 3,
    icon: '/image/welcome-icon_support.png',
    title: '安心のサポート',
    text: `お買い物の詳細は、ガイドを<br />
ご覧ください。Web・お電話・FAX<br />
でのお問い合わせも受付中です♪`,
    button: { label: 'お買い物ガイド', to: '/guide', variant: 'outline' },
  },
  {
    point: 4,
    icon: '/image/img_guide006.png',
    title: 'お買い物で迷った時は<br class="sp-only" />「よくあるご質問」もチェック！',
    text: `お客さまから寄せられる、よくあるご質問を<br class="sp-only" />まとめました。<br class="pc-only" />
ご不明な点がございましたら、<br class="sp-only" />まずはご確認ください。`,
    button: { label: 'よくあるご質問', to: '/faq', variant: 'outline' },
    variant: 'faq',
  },
] as const;

/**
 * 早速商品を探す。支給の `product-search`。
 *
 * **支給は4枚とも `./products/category` を指しているが、カテゴリ別一覧ページは作らない**（決定#23）。
 * ハンバーガーのカテゴリと同じく、トップの「人気の商品ランキング」へ該当タブを選んだ状態で飛ばす
 * （決定#26 の拡張。2026-08-13 事業側判断）。表示名と並び順は `ProductContentConst` が正なので
 * ここには持たず `categoryId` だけ持つ。並びは支給と `CATEGORY_DISPLAY_ORDER` が一致している
 * （サプリメント1 → 食品4 → スキンケア2 → パーソナルケア3）。
 */
export const WELCOME_CATEGORY_CARDS: ReadonlyArray<WelcomeCategoryCard> = [
  {
    categoryId: 1,
    icon: '/image/welcome/welcome-icon_functional-food.png',
    image: '/image/welcome/img_page-welcome002.png',
  },
  {
    categoryId: 4,
    icon: '/image/welcome/welcome-icon_food.png',
    image: '/image/welcome/img_page-welcome003.png',
  },
  {
    categoryId: 2,
    icon: '/image/welcome/welcome-icon_skincare.png',
    image: '/image/welcome/img_page-welcome004.png',
  },
  {
    categoryId: 3,
    icon: '/image/welcome/welcome-icon_personalcare.png',
    image: '/image/welcome/img_page-welcome005.png',
  },
] as const;

/**
 * お客様の声から探す。支給の `voice-search`
 *
 * 画像は `/image/banner/` の1系統だけを使う。支給には同じ絵柄が `assets/images/` 直下にも入っているが、
 * そちらは差し替え前のまま（掲載を取り下げた方が写っている）なので取り込んでいない。
 */
export const WELCOME_VOICE_SEARCH = {
  image: '/image/banner/f-banner_voice.jpg',
  spImage: '/image/banner/f-banner_voice_sp.jpg',
  button: { label: 'ユーザーの声を見る', to: '/voice' },
} as const;
